import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  BarChart3, Users, Mail, MousePointerClick, Eye, TrendingUp,
  Megaphone, Search, Loader2, RefreshCw, ArrowDown, Linkedin,
  Twitter, MessageCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

function StatCard({ icon: Icon, label, value, sub, color = 'text-gray-900', bgColor = 'bg-gray-50' }) {
  return (
    <Card>
      <CardContent className="py-4 px-5">
        <div className="flex items-center gap-3">
          <div className={`${bgColor} rounded-lg p-2.5`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 w-32 text-right">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-7 relative overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
        <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
          {value.toLocaleString()} ({pct}%)
        </span>
      </div>
    </div>
  );
}

function MiniBarChart({ data, valueKey, maxHeight = 80 }) {
  if (!data || data.length === 0) return <p className="text-xs text-gray-400 py-4 text-center">No data</p>;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="flex items-end gap-1 justify-between" style={{ height: maxHeight }}>
      {data.slice(-14).map((d, i) => {
        const val = d[valueKey] || 0;
        const h = Math.max((val / maxVal) * maxHeight, 2);
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-default"
              style={{ height: h }}
              title={`${d.date}: ${val}`}
            />
            {data.length <= 14 && (
              <span className="text-[9px] text-gray-400">{d.date?.slice(5)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function MarketingDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/marketing-dashboard/stats?days=${days}`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      console.error('Failed to load marketing dashboard', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchStats(); }, [days]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const f = stats?.funnel || {};
  const email = stats?.email_performance || {};
  const reddit = stats?.reddit_summary || {};
  const social = stats?.social_summary || {};
  const funnelMax = f.discovered || 1;

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-100 rounded-lg p-2">
            <BarChart3 className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Marketing Dashboard</h1>
            <p className="text-sm text-gray-500">Aggregated metrics across all marketing channels</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {PERIOD_OPTIONS.map((o) => (
            <Button
              key={o.value}
              variant={days === o.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(o.value)}
            >
              {o.label}
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <StatCard icon={Users} label="Leads Discovered" value={f.discovered || 0} bgColor="bg-blue-50" color="text-blue-600" />
        <StatCard icon={Mail} label="With Email" value={f.with_email || 0} bgColor="bg-green-50" color="text-green-600" />
        <StatCard icon={TrendingUp} label="Promoted to CRM" value={f.promoted_to_crm || 0} bgColor="bg-purple-50" color="text-purple-600" />
        <StatCard icon={Eye} label="Emails Sent" value={email.total_sent || 0} bgColor="bg-amber-50" color="text-amber-600" />
        <StatCard icon={MousePointerClick} label="Open Rate" value={`${email.open_rate || 0}%`} bgColor="bg-emerald-50" color="text-emerald-600" />
        <StatCard icon={MessageCircle} label="Reddit Mentions" value={reddit.total_mentions || 0} bgColor="bg-orange-50" color="text-orange-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Lead Funnel */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowDown className="w-4 h-4" /> Lead Funnel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <FunnelBar label="Discovered" value={f.discovered || 0} max={funnelMax} color="bg-blue-500" />
            <FunnelBar label="Enriched" value={f.enriched || 0} max={funnelMax} color="bg-sky-500" />
            <FunnelBar label="With Email" value={f.with_email || 0} max={funnelMax} color="bg-green-500" />
            <FunnelBar label="Promoted" value={f.promoted_to_crm || 0} max={funnelMax} color="bg-purple-500" />
            <FunnelBar label="Qualified" value={f.qualified || 0} max={funnelMax} color="bg-indigo-600" />
          </CardContent>
        </Card>

        {/* Email Performance */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4" /> Email Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <p className="text-xl font-bold text-gray-900">{email.total_sent || 0}</p>
                <p className="text-xs text-gray-500">Sent</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-600">{email.open_rate || 0}%</p>
                <p className="text-xs text-gray-500">Open Rate</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-blue-600">{email.click_rate || 0}%</p>
                <p className="text-xs text-gray-500">Click Rate</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-2">Daily emails sent (last 14 days)</p>
            <MiniBarChart data={stats?.email_trend || []} valueKey="sent" />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* SEO Score Trend */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="w-4 h-4" /> SEO Score Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.seo_trend || []).length > 0 ? (
              <div className="space-y-2">
                {stats.seo_trend.slice(0, 5).map((s, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{s.date}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-100 rounded-full h-2">
                        <div
                          className={`h-full rounded-full ${s.overall >= 80 ? 'bg-green-500' : s.overall >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${s.overall}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-10 text-right">{s.overall}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">No SEO audits yet</p>
            )}
          </CardContent>
        </Card>

        {/* Reddit Intelligence */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Reddit Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-4">
              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                +{reddit.positive || 0}
              </Badge>
              <Badge variant="outline" className="text-gray-600 border-gray-200 bg-gray-50">
                ~{reddit.neutral || 0}
              </Badge>
              <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                -{reddit.negative || 0}
              </Badge>
              {reddit.unreviewed > 0 && (
                <Badge className="bg-amber-100 text-amber-700">{reddit.unreviewed} unreviewed</Badge>
              )}
            </div>
            <p className="text-xs text-gray-400 mb-2">Top competitors mentioned</p>
            <div className="space-y-1.5">
              {(reddit.by_competitor || []).slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{c.name}</span>
                  <span className="text-sm font-medium text-gray-900">{c.count}</span>
                </div>
              ))}
              {(!reddit.by_competitor || reddit.by_competitor.length === 0) && (
                <p className="text-sm text-gray-400 py-2 text-center">No mentions yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Social Posting */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Megaphone className="w-4 h-4" /> Social Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <p className="text-xl font-bold text-gray-900">{social.total_posts || 0}</p>
                <p className="text-xs text-gray-500">Total Posts</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                  <p className="text-xl font-bold text-gray-900">{social.linkedin || 0}</p>
                </div>
                <p className="text-xs text-gray-500">LinkedIn</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Twitter className="w-4 h-4 text-gray-800" />
                  <p className="text-xl font-bold text-gray-900">{social.twitter || 0}</p>
                </div>
                <p className="text-xs text-gray-500">X / Twitter</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Attribution & Campaign Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Channel Attribution */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Channel Attribution</CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.channel_attribution || []).length > 0 ? (
              <div className="space-y-2">
                {stats.channel_attribution.map((ch, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 truncate max-w-[200px]">{ch.source}</span>
                    <Badge variant="outline">{ch.leads} leads</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">No attributed leads yet</p>
            )}
          </CardContent>
        </Card>

        {/* Campaign Performance */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Campaign Performance</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(stats?.campaigns || []).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-2.5 px-4 font-medium text-gray-600">Campaign</th>
                      <th className="text-right py-2.5 px-4 font-medium text-gray-600">Found</th>
                      <th className="text-right py-2.5 px-4 font-medium text-gray-600">Enriched</th>
                      <th className="text-right py-2.5 px-4 font-medium text-gray-600">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.campaigns.map((c, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 px-4 text-gray-900 truncate max-w-[200px]">{c.name}</td>
                        <td className="py-2 px-4 text-right text-gray-700">{c.leads_discovered}</td>
                        <td className="py-2 px-4 text-right text-gray-700">{c.leads_enriched}</td>
                        <td className="py-2 px-4 text-right text-gray-700">{c.leads_with_email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">No campaigns yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
